import { getItemType } from "../../orderCommons/index.js";

type OrderRuleKey = string;

type OrderItem = {
  quantity: number;
  containerType: string | null;
  volume: number | null;
  unit: string | null;
  packQuantity: number | null;
};

type OrderRule = {
  itemType: "keg" | "can" | "cask" | "bottle" | "draught" | "any" | "bagInBox";
  quantity: number;
  volume?: number | null;
  unit?: "gal" | "L" | "ml" | "cl" | "pt" | "oz" | "g" | "kg" | null;
};

export const haveOrderRulesBeenFulfilled = ({
  orderItemList,
  orderRuleList,
}: {
  orderItemList: OrderItem[];
  orderRuleList: OrderRule[];
}) => {
  // create order rule group map, by default applicable order item quantity is undefined

  const orderRuleGroup = new Map<
    OrderRuleKey,
    { orderRule: OrderRule; orderQuantity: number | undefined }
  >();

  for (const rule of orderRuleList) {
    // order rule duplicates removed (can happen as not enforced in db), leaves largest min quantity order rule

    const duplicateRule = orderRuleGroup.get(rule.itemType);
    if (duplicateRule) {
      const duplicateRuleQuantity = duplicateRule.orderRule.quantity;
      const currentRuleQuantity = rule.quantity;

      orderRuleGroup.set(rule.itemType, {
        orderRule: {
          itemType: rule.itemType,
          quantity:
            duplicateRuleQuantity > currentRuleQuantity
              ? duplicateRuleQuantity
              : currentRuleQuantity,
        },
        orderQuantity: undefined,
      });

      continue;
    }

    orderRuleGroup.set(rule.itemType, {
      orderRule: rule,
      orderQuantity: undefined,
    });
  }

  // iterate through order items and update order rule map with quantities of applicable order items

  for (const orderItem of orderItemList) {
    if (!orderItem.containerType?.length) {
      continue;
    }

    const orderItemType = getItemType(orderItem.containerType);
    if (!orderItemType) {
      continue;
    }

    // only integer quantities contribute to order rule
    const orderItemQuantity = Math.floor(orderItem.quantity);

    // find exact match order rule in map

    const itemTypeOrderRuleFound = orderRuleGroup.get(orderItemType);

    if (itemTypeOrderRuleFound) {
      const ruleUnit = itemTypeOrderRuleFound.orderRule.unit;
      const ruleVolume = itemTypeOrderRuleFound.orderRule.volume;
      const shouldCheckContainerSize = ruleUnit && ruleVolume;
      const matchesContainerSizeOrAbove = shouldCheckContainerSize
        ? orderItem.unit === ruleUnit && (orderItem.volume ?? 0) >= ruleVolume
        : true;

      if (matchesContainerSizeOrAbove) {
        const currentQuantityOrdered =
          itemTypeOrderRuleFound.orderQuantity ?? 0;
        orderRuleGroup.set(orderItemType, {
          ...itemTypeOrderRuleFound,
          orderQuantity: currentQuantityOrdered + orderItemQuantity,
        });
      }
    }

    // find a 'draught' order rule in map, applies to order items of type 'cask' or 'keg'

    const draughtOrderItemRuleFound = orderRuleGroup.get("draught");
    if (draughtOrderItemRuleFound && ["cask", "keg"].includes(orderItemType)) {
      const currentQuantityOrdered =
        draughtOrderItemRuleFound.orderQuantity ?? 0;
      orderRuleGroup.set("draught", {
        ...draughtOrderItemRuleFound,
        orderQuantity: currentQuantityOrdered + orderItemQuantity,
      });
    }

    // find an 'any' order rule in map, always applies to order

    const minOrderItemsRuleFound = orderRuleGroup.get("any");
    if (minOrderItemsRuleFound) {
      const currentQuantityOrdered = minOrderItemsRuleFound.orderQuantity ?? 0;
      orderRuleGroup.set("any", {
        ...minOrderItemsRuleFound,
        orderQuantity: currentQuantityOrdered + orderItemQuantity,
      });
    }
  }

  // check all applicable order rules in map have been satisfied and aggregate the result

  const orderRuleGroupAsArray = Array.from(
    orderRuleGroup,
    ([, value]) => value,
  );

  return orderRuleGroupAsArray.reduce((haveRulesBeenFulfilled, currentRule) => {
    const orderQuantity = currentRule.orderQuantity; // if undefined orderQuantity then none of this item type have been ordered
    const orderRuleItemType = currentRule.orderRule.itemType;
    const orderRuleMinQuantity = currentRule.orderRule.quantity;

    // 'any' minimum order quantity rule always applies to an order, check order quantity satisfies the min order quantity

    if (orderRuleItemType === "any") {
      const currentRuleMet =
        typeof orderQuantity === "number"
          ? orderQuantity >= orderRuleMinQuantity
          : false;

      return haveRulesBeenFulfilled && currentRuleMet;
    }

    // no applicable order items for order rule found, skip rule

    if (orderQuantity === undefined) {
      return haveRulesBeenFulfilled;
    }

    // applicable order items found for order rule found, check order quantity satisfies the min order quantity

    const currentRuleMet = orderQuantity >= orderRuleMinQuantity;
    return haveRulesBeenFulfilled && currentRuleMet;
  }, true);
};
